import { Spin, Image as AntImage } from 'antd';
import PropTypes from 'prop-types';
import React from 'react';
import ReactGridLayout from 'react-grid-layout';
import styled, { createGlobalStyle } from 'styled-components';

import { SIDE_NAV_PROPERTY } from 'common/hooks/useSideNavExpanded';
import useStableLocation from 'common/hooks/useStableLocation';

import BadgeSling from '@assets/badge-sling.svg';
import BorderImage from '@assets/border-image.png';
import { nullablePropType } from '@services/proptypes-service';

import { BadgeTypes } from '../constants';
import { BuilderBadgePropType } from '../reducer';

import {
  BADGE_PREVIEW_BOX_SHADOW,
  COLUMN_SIZE,
  GRAY_BACKGROUND_COLOR,
  GRAY_BORDER_COLOR,
  GREEN_BORDER_COLOR,
  MARGIN,
  PADDING,
  RED_BORDER_COLOR,
  SIMPLE_LABEL_PADDING,
  TYPE_IMAGE,
  TYPE_PERSONAL_CODE,
  TYPE_RICH_TEXT,
  TYPE_SHAPE,
  UNIT_DIVISION,
} from './constants';
import * as Widgets from './Widgets';

// react-resizable is a direct dep of react-grid-layout, we don't otherwise use it
// eslint-disable-next-line import/no-extraneous-dependencies
import 'react-resizable/css/styles.css';
import 'react-grid-layout/css/styles.css';

const Body = styled.div`
  z-index: 0;
  display: flex;
  justify-content: center;

  cursor: ${props => (props.$panning ? 'grab' : 'default')};
  ${props => props.$panning && '&:active { cursor: grabbing; }'}
`;

const Spinner = styled.div`
  position: fixed;
  height: 100%;
  width: calc(100% - var(${SIDE_NAV_PROPERTY}));
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SlingImages = styled.div`
  display: flex;
  position: absolute;
  transform: translateY(-90px);
  z-index: 100;
  gap: ${props => props.slingGap}px;
`;

const Image = styled(AntImage)`
  object-fit: contain;
`;

const Style = createGlobalStyle`
  .react-grid-item.react-grid-placeholder {
    && {
      background: #000000;
      opacity: 0.2;
      border-radius: 2px;

      // Workaround for a bug introduced in react-grid-layout 1.4.3 - the onClick
      // event isn't fired unless the widget has been dragged first. From the
      // comments in the issue it sounds like they add a placeholder element to
      // the DOM and this version changed where/when it's added.
      //
      // https://github.com/react-grid-layout/react-grid-layout/issues/1980
      pointer-events: none;
    }
  }

  .react-draggable-dragging {
    box-shadow: 0 19px 38px rgba(0, 0, 0, 0.30), 0 15px 12px rgba(0, 0, 0, 0.22);
  }

  .react-resizable-handle {
    position: absolute;
    width: 20px;
    height: 20px;
    background-repeat: no-repeat;
    background-origin: content-box;
    box-sizing: border-box;
    background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTMiIGhlaWdodD0iMTMiIHZpZXdCb3g9IjAgMCAxMyAxMyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cGF0aCBkPSJNOCAxSDdWMlY2LjVIMkgxVjcuNVYxMC41VjExLjVIMkgxMUgxMlYxMC41VjJWMUgxMUg4WiIgZmlsbD0iIzU5NTk1OSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIyIi8+Cjwvc3ZnPgo=);
    background-position: bottom right;
    padding: 5px;

    &&::after {
      border: none;
    }
  }
  /*
  When scaling, the grid item position jumps occur because of transition property applied to grid items.
  */
  .react-grid-item {
    transition: none;
  }
`;

const Wrap = styled.div`
  width: ${props => props.width}px;
  height: ${props => props.height}px;
  /* This is needed to prevent RTE in a label export from rendering duplicate pages */
  overflow: hidden;

  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;

  background-color: ${props => props.color};
  background-image: url('${props => props.image?.url ?? ''}');
  background-attachment: initial;
  background-size: ${({ size }) => size};

  position: relative;
`;

const EditorPreviewDropShadow = styled.div`
  &:after {
    pointer-events: none;
    content: '';
    ${({ $bleedAndBorderGap }) =>
      $bleedAndBorderGap
        ? // If have a bleed/border gap, we also apply a flat grey dropshadow to crop the content off
          `box-shadow: ${BADGE_PREVIEW_BOX_SHADOW}, 0 0 0 ${$bleedAndBorderGap}px ${GRAY_BACKGROUND_COLOR};`
        : `box-shadow: ${BADGE_PREVIEW_BOX_SHADOW};`}
    position: absolute;
    ${props =>
      props.$bleedAndBorderGap
        ? // If we have a bleed/border gap, inset the shadow by the gap
          `inset: ${props.$bleedAndBorderGap}px;`
        : 'inset: 0;'}
    z-index: 10;
  }
`;

const DesignBorder = styled.div`
  // Hide the overflow so that bleed doesn't show in previews
  overflow: hidden;
  position: relative;

  ${({ $hideOuter, $outerColor }) => `
    // We use a :before pseudo-element to create an overlayed outer border without affecting the
    // size of the badge.
    &:before {
      pointer-events: none;
      content: '';
      position: absolute;
      inset: 0;
      border: ${$hideOuter ? 0 : '1px'} solid ${$outerColor || GRAY_BORDER_COLOR};
      z-index: 5;
    }
  `}

  ${({ $innerColor, $innerOffset }) =>
    $innerColor &&
    $innerOffset &&
    `
    &:after {
      pointer-events: none;
      content: '';
      position: absolute;
      inset: ${$innerOffset}px;
      border: 1px solid ${$innerColor};
    }
  `}
`;

const WIDGET_BORDER_OFFSET_PX = 2;

const WidgetBorder = styled.div`
  position: absolute;
  top: -${WIDGET_BORDER_OFFSET_PX}px;
  left: -${WIDGET_BORDER_OFFSET_PX}px;
  width: calc(100% + ${2 * WIDGET_BORDER_OFFSET_PX}px);
  height: calc(100% + ${2 * WIDGET_BORDER_OFFSET_PX}px);
`;

const Base = styled.div`
  ${
    /* eslint-disable-next-line no-nested-ternary */
    props => (props.readonly ? '&' : !props.selected ? '&:not(:hover)' : 'none-existing-selector')
  } {
    .react-resizable-handle::after {
      content: unset;
    }
    .react-resizable-handle {
      background-image: none;
    }
  }

  ${
    /* eslint-disable-next-line no-nested-ternary */
    props => (props.readonly ? 'none-existing-selector' : !props.selected ? '&:hover' : '&')
  } {
    ${WidgetBorder} {
      border: dashed 2px;
      border-image-slice: 2;
      border-image-repeat: round;
      border-image-source: url(${BorderImage});
    }
  }
`;

const fixedMargin = `${SIMPLE_LABEL_PADDING.VERTICAL}px`;
const userMargin = `${SIMPLE_LABEL_PADDING.HORIZONTAL}px`;

const SimpleLabelMargins = styled.div`
  background: white;
  padding: ${({ $orientation }) =>
      $orientation === 'vertical'
        ? `${userMargin} ${fixedMargin};`
        : `${fixedMargin} ${userMargin};`}
    ${({ $showMarginBorder, $orientation }) =>
      $showMarginBorder &&
      `
    &:after {
      pointer-events: none;
      content: '';
      position: absolute;
      ${
        $orientation === 'vertical'
          ? `inset: ${userMargin} ${fixedMargin};`
          : `inset: ${fixedMargin} ${userMargin};`
      }
      border: 1px solid ${GREEN_BORDER_COLOR};
    }
  `};
`;

const TrimBleed = styled.div`
  ${({ $bleedAndBorderGap }) => $bleedAndBorderGap && `margin: -${$bleedAndBorderGap}px;`}
`;

const Slings = ({ badgeSize }) => {
  const badgeWidth = UNIT_DIVISION[badgeSize.unit] * badgeSize.innerBadgeSize.width * COLUMN_SIZE;
  const slingGap = Math.max(80, badgeWidth * 0.55);

  return (
    <SlingImages slingGap={slingGap}>
      <Image width={35} height={121} src={BadgeSling} preview={false} alt="Sling Image" />
      <Image width={35} height={121} src={BadgeSling} preview={false} alt="Sling Image" />
    </SlingImages>
  );
};

Slings.propTypes = {
  badgeSize: PropTypes.shape({
    innerBadgeSize: PropTypes.shape({
      width: PropTypes.number.isRequired,
    }),
    unit: PropTypes.string.isRequired,
  }).isRequired,
};

const BorderBleedWrapper = ({
  children,
  showBorder,
  isBleedEnabled,
  isPreviewMode,
  isSimpleLabelBadge,
  size,
  orientation,
}) => {
  const location = useStableLocation();
  const isCaptureExport = location.pathname.includes('/pdf-capture/badges/');
  const isCapturePreview = location.pathname.includes('/capture/badges/');

  const { width, unit, innerBadgeSize } = size;
  const badgeWidth = innerBadgeSize.width * UNIT_DIVISION[unit];
  const containerWidth = width * UNIT_DIVISION[unit] * COLUMN_SIZE;
  // gap between the badges and bleed
  const bleedAndBorderGap = (containerWidth - badgeWidth * COLUMN_SIZE) / 2;

  let result = children;

  if (isCaptureExport) {
    // The only time we show a border when capturing an export is if its a badge with showBorder on
    if (!isSimpleLabelBadge && showBorder) {
      if (isBleedEnabled) {
        // When bleed is enabled, we need to show the inner border as the badge border
        result = (
          <DesignBorder $hideOuter $innerColor={GRAY_BORDER_COLOR} $innerOffset={bleedAndBorderGap}>
            {result}
          </DesignBorder>
        );
      } else {
        result = (
          <DesignBorder $outerColor={GRAY_BORDER_COLOR} $hideOuter={false}>
            {result}
          </DesignBorder>
        );
      }
    }
  } else if (isSimpleLabelBadge) {
    // Render the margin, and render the green margin border if we're not in a preview mode
    result = (
      <SimpleLabelMargins
        $orientation={orientation}
        $showMarginBorder={!isPreviewMode && !isCapturePreview}
      >
        {result}
      </SimpleLabelMargins>
    );
    // Add the border around the outside
    result = <DesignBorder $outerColor={GRAY_BORDER_COLOR}>{result}</DesignBorder>;
  } else if (isCapturePreview) {
    // If bleed is enabled, we need to trim off the bleed content for the preview
    if (isBleedEnabled) {
      result = <TrimBleed $bleedAndBorderGap={bleedAndBorderGap}>{result}</TrimBleed>;
    }
    // If we're capturing a preview image, we only show the outside border
    result = <DesignBorder $outerColor={GRAY_BORDER_COLOR}>{result}</DesignBorder>;
  } else {
    // This must be a badge in either edit or preview mode, so we should add the badge border and
    // optionally the bleed border
    result = (
      <DesignBorder
        $outerColor={bleedAndBorderGap ? RED_BORDER_COLOR : GRAY_BORDER_COLOR}
        $innerColor={bleedAndBorderGap ? GRAY_BORDER_COLOR : ''}
        $innerOffset={bleedAndBorderGap}
      >
        {result}
      </DesignBorder>
    );
  }

  if (isPreviewMode) {
    result = (
      <EditorPreviewDropShadow $bleedAndBorderGap={bleedAndBorderGap}>
        {result}
      </EditorPreviewDropShadow>
    );
  }

  return result;
};

BorderBleedWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  showBorder: PropTypes.bool.isRequired,
  isBleedEnabled: PropTypes.bool.isRequired,
  isPreviewMode: PropTypes.bool.isRequired,
  isSimpleLabelBadge: PropTypes.bool.isRequired,
  size: PropTypes.shape({
    width: PropTypes.number.isRequired,
    unit: PropTypes.string.isRequired,
    innerBadgeSize: PropTypes.shape({
      width: PropTypes.number.isRequired,
      height: PropTypes.number.isRequired,
    }),
  }).isRequired,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']).isRequired,
};

// TODO: Once the reducer is gone, refactor this to separate 'badge' and 'design'
// props
const Builder = ({
  badge,
  person,
  showBorder,
  isLoading,

  // Editor props
  readonly = true,
  isPreviewMode = false,
  scale = 1,
  isPanning = false,
  selectedWidgetId = null,
  handleChange = null,
  handleSelectWidget = null,
  handleResizeStop = null,
}) => {
  const { width, height, cols, rows: maxRows, unit } = badge.size;

  const containerWidth = width * UNIT_DIVISION[unit] * COLUMN_SIZE;
  const containerHeight = height * UNIT_DIVISION[unit] * COLUMN_SIZE;

  // TODO: Parents should handle this, and capture should look for something
  // on the actual content
  if (isLoading) {
    return (
      <Spinner>
        <Spin />
      </Spinner>
    );
  }

  return (
    <Body $panning={isPanning}>
      {isPreviewMode && badge.badgeType !== BadgeTypes.SIMPLE_LABEL && (
        <Slings badgeSize={badge.size} />
      )}
      <BorderBleedWrapper
        showBorder={showBorder}
        isBleedEnabled={badge.isBleedEnabled}
        isPreviewMode={isPreviewMode}
        isSimpleLabelBadge={badge.badgeType === BadgeTypes.SIMPLE_LABEL}
        size={badge.size}
        orientation={badge.orientation}
      >
        <Wrap {...badge.background} width={containerWidth} height={containerHeight}>
          <Style />
          <ReactGridLayout
            allowOverlap
            // adding `preventCollision` fixes the issue where dragging one widget would
            // cause other overlapping widgets to move a little
            preventCollision
            maxRows={maxRows}
            width={containerWidth}
            cols={cols}
            rowHeight={containerWidth / cols}
            margin={MARGIN}
            containerPadding={PADDING}
            onDragStart={
              readonly ? null : (_layout, _oldItem, newItem) => handleSelectWidget?.(newItem.i)
            }
            // onDragStop and onResizeStop are here as workaround until the following issue is fixed in react-grid-layout
            // https://github.com/react-grid-layout/react-grid-layout/issues/1775
            // When fixed, we should use `onLayoutChange={layout => onChange(layout)}` instead
            onDragStop={readonly ? null : handleChange}
            onResizeStop={readonly ? null : handleResizeStop}
            readonly={readonly}
            transformScale={scale}
            // Force complete remount when scale changes to ensure
            // correct widget positioning and transform calculations
            key={scale}
          >
            {badge.widgets.map(({ id: key, type, layout, params }) => (
              <Base
                key={key}
                readonly={readonly}
                selected={selectedWidgetId === key}
                onClick={readonly ? null : () => handleSelectWidget?.(key)}
                data-grid={{ ...layout, isDraggable: !readonly, isResizable: !readonly }}
              >
                <div style={{ width: 'inherit', height: 'inherit' }}>
                  {type === TYPE_IMAGE && (
                    <Widgets.Image {...params} key={key} src={params?.image?.url} />
                  )}
                  {type === TYPE_RICH_TEXT && (
                    <Widgets.Text key={key} readOnly={readonly} person={person} {...params} />
                  )}
                  {type === TYPE_SHAPE && (
                    <Widgets.Shape key={key} width={layout.w} height={layout.h} {...params} />
                  )}
                  {type === TYPE_PERSONAL_CODE && (
                    <Widgets.PersonalCode
                      key={key}
                      readOnly={readonly}
                      width={layout.w}
                      height={layout.h}
                      person={person}
                      {...params}
                    />
                  )}
                </div>
                {!readonly && <WidgetBorder />}
              </Base>
            ))}
          </ReactGridLayout>
        </Wrap>
      </BorderBleedWrapper>
    </Body>
  );
};

Builder.propTypes = {
  badge: BuilderBadgePropType.isRequired,
  person: nullablePropType(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
      companyName: PropTypes.string,
      title: PropTypes.string,
      checkinCode: PropTypes.string,
      customFields: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string.isRequired,
          value: PropTypes.string,
        }),
      ).isRequired,
    }),
  ).isRequired,
  showBorder: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,

  // Editor only
  readonly: PropTypes.bool,
  selectedWidgetId: PropTypes.string,
  isPreviewMode: PropTypes.bool,
  scale: PropTypes.number,
  isPanning: PropTypes.bool,
  handleChange: PropTypes.func,
  handleSelectWidget: PropTypes.func,
  handleResizeStop: PropTypes.func,
};

export default Builder;
